import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { CheckCheck } from "lucide-react"
import { useState } from "react"
import { Link, useNavigate } from "react-router"

import { api, ErrorApi } from "@/api/cliente"
import { type Aviso, destinoDe, ListaDeAvisos } from "@/componentes/avisos/ListaDeAvisos"
import { Pestanas } from "@/componentes/ui/Pestanas"

/**
 * La bandeja entera.
 *
 * ⚠ **No es el buzón.** El buzón son las conversaciones con los importadores; esto
 * es lo que le pasa al trabajo de uno. Comparten la palabra y nada más, y por eso
 * están en dos sitios de la barra lateral.
 *
 * ⚠ **Marcar todos no borra nada.** El histórico se conserva: la bandeja es también
 * el sitio donde se mira «¿qué me dijeron la semana pasada de esta tarea?».
 */

const VISTAS = [
  { valor: "sin-leer", etiqueta: "Sin leer" },
  { valor: "todos", etiqueta: "Todos" },
] as const

type Vista = (typeof VISTAS)[number]["valor"]

interface Pagina {
  total: number
  no_leidos: number
  items: Aviso[]
}

export function Avisos() {
  const [vista, setVista] = useState<Vista>("sin-leer")
  const clienteQuery = useQueryClient()
  const navegar = useNavigate()

  const consulta = useQuery({
    queryKey: ["avisos", "pagina", vista],
    queryFn: () => api.get<Pagina>(`/api/v1/avisos?limit=100&no_leidos=${vista === "sin-leer"}`),
    refetchInterval: 60_000,
  })

  const marcar = useMutation({
    // Sin estado de error en la pantalla: fallaba en silencio (auditoría 12/10).
    meta: { error: "No se pudo marcar el aviso como leído" },
    mutationFn: (id: number) => api.post(`/api/v1/avisos/${id}/leido`),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: ["avisos"] }),
  })

  const marcarTodos = useMutation({
    // Sin estado de error en la pantalla: fallaba en silencio (auditoría 12/10).
    meta: { error: "No se pudieron marcar los avisos como leídos" },
    mutationFn: () => api.post("/api/v1/avisos/leidos"),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: ["avisos"] }),
  })

  const abrir = (aviso: Aviso) => {
    marcar.mutate(aviso.id)
    const destino = destinoDe(aviso)
    if (destino !== null) navegar(destino)
  }

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Avisos</h1>
          <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
            {consulta.data ? `${consulta.data.no_leidos} sin leer` : " "}
          </p>
        </div>
        <button
          type="button"
          onClick={() => marcarTodos.mutate()}
          disabled={marcarTodos.isPending || (consulta.data?.no_leidos ?? 0) === 0}
          className="boton-secundario flex h-9 items-center gap-2 px-3 text-sm disabled:opacity-50"
        >
          <CheckCheck size={16} aria-hidden="true" />
          Marcar todos como leídos
        </button>
      </div>

      <Pestanas
        pestanas={VISTAS}
        activa={vista}
        onCambiar={(v) => setVista(v as Vista)}
        etiqueta="Qué avisos se muestran"
        idPanel="panel-avisos"
        prefijoId="pestana-avisos"
      />

      {/* ⚠ **La superficie va AQUÍ y no dentro de `ListaDeAvisos`**, y es la razón
          de que faltara: esa lista se usa también dentro del desplegable de la
          campanita, que ya trae su propio recuadro con borde y sombra. Metiéndosela
          al componente, la campanita tendría dos cajas anidadas; sin ponerla en
          ningún sitio —que es lo que pasaba— la pantalla completa dejaba la bandeja
          cayendo sobre el lienzo, y las lengüetas de las pestañas, que están
          dibujadas para unirse al contenido, se unían a nada. */}
      <div
        id="panel-avisos"
        role="tabpanel"
        aria-labelledby={`pestana-avisos-${vista}`}
        className="superficie p-1"
      >
        {consulta.isPending && (
          <p className="py-12 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
            Cargando avisos…
          </p>
        )}
        {consulta.isError && (
          <p
            role="alert"
            className="py-12 text-center text-sm"
            style={{ color: "var(--destructive)" }}
          >
            {consulta.error instanceof ErrorApi
              ? consulta.error.message
              : "No se pudieron cargar los avisos"}
          </p>
        )}
        {consulta.data && <ListaDeAvisos avisos={consulta.data.items} onAbrir={abrir} />}
      </div>

      {/* El enlace con Telegram se mudó a «Mi cuenta»: es un ajuste de la cuenta de
          uno, como la contraseña, y no una bandeja que se lee. Aquí queda el camino,
          porque es mirando los avisos cuando a alguien se le ocurre quererlos en el
          móvil. */}
      <p className="mt-6 text-sm" style={{ color: "var(--fg-muted)" }}>
        ¿Los quieres también en Telegram?{" "}
        <Link to="/perfil" className="underline underline-offset-2">
          Enlaza tu cuenta
        </Link>
        .
      </p>
    </div>
  )
}
